/*

  Global Slider Component
  ------------------------

  A class representing a global slider component that uses the Keen Slider library. It allows customization through data-attributes set on the slider container. The slider is responsive and can have different number of slides displayed depending on the screen size. It also supports navigation and controls.

  Note** data-attributes should be set on slider container to control the following options:

  maxWidth <data-max-width> slider breaks after this screen width ( default none )
  minWidth <data-min-width> slider breaks before this screen width ( default 0 )
  perView desktop <data-per-view-desktop> how many slides will display on desktop ( default 1 )
  perView mobile <data-per-view-mobile> how many slides will display on mobile/tablet ( default 1 )
  hasLoop <data-slider-loop> does slider loopp (default false)


*/

import "keen-slider/keen-slider.min.css";
import KeenSlider from "keen-slider";

export class Slider {
  /*
  Creates an instance of Slider.
  @constructor
  @param {HTMLElement} container - The HTML element that contains the slider.

*/
  constructor(container) {
    this.breakpoint = 1024;
    this.key = container.dataset.key;

    /**
    An object containing information about the slider.
      @property {HTMLElement} container - The HTML element that contains the slider.
      @property {NodeListOf<HTMLElement>} slides - The HTML elements representing the slides.
      @property {KeenSlider} instance - The instance of the KeenSlider library used to create the slider.
      @property {number} maxWidth - The screen width at which the slider stops rendering (default none).
      @property {number} minWidth - The screen width at which the slider starts rendering (default 0).
      @property {object} perView - An object that contains the number of slides to display depending on the screen size.
      @property {number} perView.desktop - The number of slides to display on desktop (default 1).
      @property {number} perView.mobile - The number of slides to display on mobile/tablet (default 1).
    */
    this.slider = {
      container,
      slides: container.querySelectorAll(".keen-slider__slide"),
      instance: false,
      maxWidth: false,
      minWidth: false,
      renderMode: false,
      autoRotate: false,
      perView: {
        desktop: false,
        tablet: false,
        mobile: false,
      },
    };

    /**
      An object containing information about the slider navigation.
      @property {HTMLElement} container - The HTML element that contains the slider navigation.
      @property {NodeListOf<HTMLElement>} links - The HTML elements representing the navigation dots.
      @property {string} fill - The background color of the navigation dot when active.
      @private
    */
    this.nav = {
      container: document.querySelector(`slider-nav[data-key=${this.key}]`),
      links: false,
      fill: false,
    };

    /**
      An object containing information about the slider controls.
      @property {HTMLElement} container - The HTML element that contains the slider controls.
      @property {HTMLElement} prev - The HTML element representing the previous control.
      @property {HTMLElement} next - The HTML element representing the next control.
    */
    this.controls = {
      container: document.querySelector(
        `slider-controls[data-key=${this.key}]`
      ),
      prev: false,
      next: false,
    };

    /**
    The current slide index.
    @type {number}
    */
    this.index = 0;

    this._init();
  }

  /**
    Builds the slider instance using the KeenSlider library.
  */
  _build = () => {
    // check if the slider is disabled and remove the attribute if it exists
    if (this.slider.container.hasAttribute("data-keen-slider-disabled")) {
      this.slider.container.removeAttribute("data-keen-slider-disabled");
    }

    // check if navigation is enabled and get the nav links and fill color
    if (this.nav.container != null) {
      this.nav.links = this.nav.container.querySelectorAll("nav-dot");
      this.nav.fill = this.nav.container.dataset.fill;
    }

    // check if controls are enabled and get the previous and next buttons
    if (this.controls.container != null) {
      this.controls.prev =
        this.controls.container.querySelector("control-prev");
      this.controls.next =
        this.controls.container.querySelector("control-next");
    }

    // check if the slider has a loop attribute
    const hasLoop = this.slider.container.hasAttribute("data-slider-loop");

    // check autoplay
    const hasAutoplay = this.slider.container.hasAttribute("data-autoplay");
    let plugins = [];

    if (hasAutoplay) {
      const autoplaySpeed =
        Number(this.slider.container.getAttribute("data-autoplay")) * 1000;
      plugins = [
        (slider) => {
          let timeout;
          let mouseOver = false;
          function clearNextTimeout() {
            clearTimeout(timeout);
          }
          function nextTimeout() {
            clearTimeout(timeout);
            if (mouseOver) return;
            timeout = setTimeout(() => {
              slider.next();
            }, autoplaySpeed);
          }
          slider.on("created", () => {
            slider.container.addEventListener("mouseover", () => {
              mouseOver = true;
              clearNextTimeout();
            });
            slider.container.addEventListener("mouseout", () => {
              mouseOver = false;
              nextTimeout();
            });
            nextTimeout();
          });
          slider.on("dragStarted", clearNextTimeout);
          slider.on("animationEnded", nextTimeout);
          slider.on("updated", nextTimeout);
        },
      ];
    }

    this.slider.renderMode = this.slider.container.hasAttribute("data-mode")
      ? this.slider.container.dataset.mode
      : "precision";

    // create a new KeenSlider instance with the appropriate options
    this.slider.instance = new KeenSlider(
      this.slider.container,
      {
        selector: this.slider.slides, // selector for the slide elements
        slides: {
          spacing: 20, // space between slides
          perView: this.slider.perView.mobile ? this.slider.perView.mobile : 1, // number of slides to show on mobile
        },
        breakpoints: {
          "(min-width: 768px)": {
            slides: {
              perView: this.slider.perView.tablet
                ? this.slider.perView.tablet
                : 1,
              spacing: 20,
            },
          },
          "(min-width: 1024px)": {
            slides: {
              perView: this.slider.perView.desktop
                ? this.slider.perView.desktop
                : 1,
              spacing: 20,
            },
          },
        },
        loop: hasLoop,
        renderMode: this.slider.renderMode,
        created: (data) => {
          // set the opacity of each slide to 1
          this.slider.slides.forEach((el) => {
            el.style.opacity = "1";
          });

          // build the navigation if it exists
          this._buildNav();

          // build the controls if they exist
          if (this.controls.container) {
            this._buildControls();
          }
        },
        slideChanged: (data) => {
          // update the slider based on the current slide index
          this._updateSlider(data.track.details.rel);
        },
        detailsChanged: (s) => {
          if (this.slider.renderMode === "custom") {
            s.slides.forEach((element, idx) => {
              element.style.opacity = s.track.details.slides[idx].portion;
            });
          }
        },
        destroyed: () => {
          // set the disabled attribute to the slider container and reset the slides
          this.slider.container.setAttribute("data-keen-slider-disabled", null);
          this._resetSlides();
        },
      },
      plugins
    );
  };

  /**
    Updates the slider by changing the currently displayed slide and updating the navigation dots, if present.
    @param {number} newIdx - The index of the slide to display.
    @returns {void}
  */
  _updateSlider = (newIdx) => {
    // this.slider.slides[this.index].setAttribute("aria-hidden", true);
    // this.slider.slides[newIdx].setAttribute("aria-hidden", false);

    if (this.slider.renderMode === "custom") {
      this.slider.slides[this.index].style.zIndex = "1";
      this.slider.slides[newIdx].style.zIndex = "5";
    }

    if (this.nav.container != null) {
      this.nav.links[this.index].style.background = "transparent";
      this.nav.links[this.index].setAttribute("aria-current", false);

      this.nav.links[newIdx].style.background = `${this.nav.fill}`;
      this.nav.links[newIdx].setAttribute("aria-current", true);
    }

    this.index = newIdx;
  };

  /**
    Handles the click event on navigation dots or controls to move the slider to the corresponding slide.
    @param {Object} e - The event object representing the click event.
  */
  _handleClick = (e) => {
    const index = e.currentTarget.dataset.key;
    this.slider.instance.moveToIdx(parseInt(index));
  };

  /**
    Handles the previous slide event
  */
  _handlePrev = () => {
    this.slider.instance.prev();
  };

  /**
    Handles the next slide event
  */
  _handleNext = () => {
    this.slider.instance.next();
  };

  /**
    Handles the keydown event
    @param {Event} e - the event object
  */
  _handleKeyDown = (e) => {
    const action = e.target.dataset.action;

    if (e.key === "Enter" && action === "prev") {
      return this._handlePrev();
    }
    if (e.key === "Enter" && action === "next") {
      return this._handleNext();
    }
    if (e.key === "ArrowLeft" && e.target.localName === "control-next") {
      return this.controls.prev.focus();
    }
    if (e.key === "ArrowRight" && e.target.localName === "control-prev") {
      return this.controls.next.focus();
    }
  };

  /**
    Builds navigation dots and adds event listener for click on each dot
  */
  _buildNav = () => {
    if (this.nav.container == null) {
      return;
    }
    this.nav.links[this.index].style.background = `${this.nav.fill}`;
    this.nav.links.forEach((el) => {
      el.addEventListener("click", this._handleClick);
    });
  };

  /**

  Sets up the slider controls by adding event listeners to the previous and next buttons.
  If the controls container is not present, returns from the function without performing any action.
  */
  _buildControls = () => {
    if (this.controls.container == null) {
      return;
    }
    this.controls.prev.addEventListener("click", this._handlePrev);
    this.controls.prev.addEventListener("keydown", this._handleKeyDown);

    this.controls.next.addEventListener("click", this._handleNext);
    this.controls.next.addEventListener("keydown", this._handleKeyDown);
  };

  /**
    Resets the slides by removing any transform applied to them and resetting aria-hidden attribute to false.
  */
  _resetSlides = () => {
    this.slider.slides.forEach((el) => {
      el.style.transform = "0";
      el.removeAttribute("aria-hidden");
      el.setAttribute("aria-hidden", false);
    });
  };

  /**
    Checks the window size against the defined minimum and maximum widths for the slider.
    If the window size meets the criteria, the slider is built.
    If not, the slider is not built.
  */
  _checkSize = () => {
    const minWidth = this.slider.minWidth;
    const maxWidth = this.slider.maxWidth;
    const windowWidth = window.innerWidth;

    if (!minWidth && !maxWidth) {
      this._build();
      return;
    }

    // If there is a minimum width and the window is wider than the minimum, rebuild the slider
    if (minWidth && windowWidth > minWidth) {
      this._build();
      return;
    }

    // If there is a maximum width and the window is narrower than the maximum, rebuild the slider
    if (maxWidth && windowWidth < maxWidth) {
      this._build();
    }
  };

  /**
    Initializes the slider based on the provided configuration in the slider object.

    Checks the screen size and builds the slider accordingly.

    Listens for window resize events and destroys the slider instance if necessary.
  */
  _init = () => {
    if (
      this.slider.slides === false ||
      this.slider.container.dataset.disabled === "true"
    ) {
      return;
    }

    // Set number of slides to show on mobile and desktop devices if specified in slider container attributes
    if (this.slider.container.hasAttribute("data-per-view-mobile")) {
      this.slider.perView.mobile = parseInt(
        this.slider.container.dataset.perViewMobile
      );
    }

    if (this.slider.container.hasAttribute("data-per-view-tablet")) {
      this.slider.perView.tablet = parseInt(
        this.slider.container.dataset.perViewTablet
      );
    }

    if (this.slider.container.hasAttribute("data-per-view-desktop")) {
      this.slider.perView.desktop = parseInt(
        this.slider.container.dataset.perViewDesktop
      );
    }

    // Set maximum and minimum screen width for the slider if specified in slider container attributes
    if (this.slider.container.hasAttribute("data-max-screen-width")) {
      this.slider.maxWidth = parseInt(
        this.slider.container.dataset.maxScreenWidth
      );
    }
    if (this.slider.container.hasAttribute("data-min-screen-width")) {
      this.slider.minWidth = parseInt(
        this.slider.container.dataset.minScreenWidth
      );
    }

    // Call the _checkSize function to set the number of slides to show based on current screen width
    this._checkSize();

    window.addEventListener("resize", () => {
      // If slider instance doesn't exist, call _checkSize function and return
      if (this.slider.instance === false) {
        this._checkSize();
        return;
      }

      // If window width is less than minimum width, destroy the slider instance
      if (
        this.slider.minWidth !== false &&
        window.innerWidth < this.slider.minWidth
      ) {
        this.slider.instance.destroy();
      }

      // If window width is greater than maximum width, destroy the slider instance
      if (
        this.slider.maxWidth !== false &&
        window.innerWidth > this.slider.maxWidth
      ) {
        this.slider.instance.destroy();
      }
    });
  };
}
